import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faProjectDiagram, faTimes, faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { money, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
import PipelineProjectService from '@frontend/AppSettings/services/PipelineProjectService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import {
  overlayPct,
  todayUtcMs,
  type GanttStatus,
  type PhaseGanttModel,
  type PhaseGanttRow,
} from './phaseGanttModel';

/**
 * The demo's CSS phase Gantt, over the model `buildPhaseGantt` derived from real payloads.
 *
 * Click a row → phase-detail popout (the project-tab's `detailAsPopout`). Hover tips are
 * omitted in this mode, matching the demo. Weather days are the recorded skip — no red
 * day-columns, no weather legend. Money cells go through `money()`, so a first paint before
 * capabilities resolve is dashes, never a figure.
 *
 * Build L (0028) adds REALITY beside the frozen plan, on two deliberate axes:
 *  - The actual BAR is geometry only (entered start → entered end, or today while open),
 *    overlaid slim under the planned bar. It borrows no `GanttStatus` colour — red stays a money
 *    claim, yellow stays FA slip, and the planned bar's own future-paint rule is untouched.
 *  - The lifecycle WORDS (Not started / In progress / Complete) come from the server's one
 *    sanctioned derivation and sit beside the phase name as plain text. Words and paint may
 *    disagree (an early first sheet is "In progress" over a still-future-painted bar) — both
 *    are true and neither is corrected.
 * The detail popout is where the PM ENTERS the actual dates (MUTATE_BIDS affordance; the server
 * gates regardless), with the sheet evidence (first/last day-sheet date) rendered beside the
 * entered pair — when they disagree, both show and neither wins silently.
 */
interface PipelinePhaseScheduleProps {
  matterid: string;
  model: PhaseGanttModel;
}

const BAR_CLASS: Record<GanttStatus, string> = {
  green: 'green',
  yellow: 'yellow',
  red: 'red',
  future: 'future',
};

const STATUS_LABEL: Record<GanttStatus, string> = {
  green: 'On track',
  yellow: 'At risk',
  red: 'Over budget',
  future: 'Not started',
};

const dayLabel = (d: number, total: number): string => {
  // Long jobs: labelling every day collides into unreadability. Keep D1, every 7th, today
  // (the parent marks `.current`), and the last day.
  if (total <= 45) return `D${d}`;
  if (d === 1 || d === total || d % 7 === 1) return `D${d}`;
  return '';
};

export function PipelinePhaseSchedule({ matterid, model }: PipelinePhaseScheduleProps): JSX.Element {
  const visible = useFinancialVisible();
  const capabilities = useBidCapabilities();
  const mayManage = capabilities?.capabilities.includes('mutate_bids') ?? false;
  const { span, rows } = model;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedId(null);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [selected]);

  const dayY = span?.dayY ?? 0;
  const dayCols = span ? Array.from({ length: span.dayY }, (_, i) => i + 1) : [];

  return (
    <div>
      <div
        className="pipeline-gantt"
        aria-label="Phase schedule"
        style={{ ['--gantt-days' as string]: String(dayY || 1) }}
      >
        {span && (
          <div className="pipeline-gantt-head">
            <div className="pipeline-gantt-head-label">Phase</div>
            <div className="pipeline-gantt-head-days">
              {dayCols.map((d) => (
                <div
                  key={d}
                  className={`pipeline-gantt-daycol${d === span.dayX ? ' current' : ''}`}
                >
                  {dayLabel(d, dayY)}
                </div>
              ))}
            </div>
          </div>
        )}

        {rows.map((row) => (
          <GanttRow
            key={row.id}
            row={row}
            dayY={dayY}
            selected={row.id === selectedId}
            visible={visible}
            onSelect={() => setSelectedId(row.id)}
          />
        ))}
      </div>

      <div className="pipeline-gantt-legend">
        <span className="pipeline-gantt-legend-item">
          <span className="pipeline-gantt-swatch" style={{ background: '#16a34a' }} />On track
        </span>
        <span className="pipeline-gantt-legend-item">
          <span className="pipeline-gantt-swatch" style={{ background: '#d97706' }} />At risk
        </span>
        <span className="pipeline-gantt-legend-item">
          <span className="pipeline-gantt-swatch" style={{ background: '#dc2626' }} />Over budget
        </span>
        <span className="pipeline-gantt-legend-item">
          <span className="pipeline-gantt-swatch" style={{ background: '#94a3b8', opacity: 0.65 }} />
          Not started
        </span>
        <span className="pipeline-gantt-legend-item">
          <span
            className="pipeline-gantt-swatch"
            style={{ border: '2px dashed #dc2626', background: 'rgba(220,38,38,0.08)' }}
          />
          FA slip
        </span>
        <span className="pipeline-gantt-legend-item">
          <span
            className="pipeline-gantt-swatch"
            style={{ border: '2px dashed #d97706', background: 'rgba(217,119,6,0.08)' }}
          />
          Knock-on push
        </span>
        <span className="pipeline-gantt-legend-item">
          <span className="pipeline-gantt-swatch" style={{ background: '#334155', height: 6 }} />
          Actual dates
        </span>
      </div>

      {selected && (
        // key is load-bearing (review F1): the date form seeds its picks ONCE per mount, and the
        // overlay has no focus trap — the rows behind it stay selectable. Without the key a
        // selection change re-rendered the same mounted modal carrying the PREVIOUS phase's picks,
        // and Save PUT them onto the new phase.
        <PhaseDetailModal
          key={selected.id}
          matterid={matterid}
          row={selected}
          visible={visible}
          mayManage={mayManage}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

const displayStatus = (row: PhaseGanttRow, visible: boolean): GanttStatus => {
  // Over-budget is a money claim — until capabilities resolve, fall back to the quantity
  // status so first paint never says "Over budget" in chrome the way money() dashes figures.
  if (row.status === 'red' && !visible) {
    return (row.slipDays > 0 || row.pushDays > 0) ? 'yellow' : 'green';
  }
  return row.status;
};

const GanttRow = ({
  row, dayY, selected, visible, onSelect,
}: {
  row: PhaseGanttRow;
  dayY: number;
  selected: boolean;
  visible: boolean;
  onSelect: () => void;
}): JSX.Element => {
  const status = displayStatus(row, visible);
  const slip = overlayPct(row.endDay, row.slipDays, dayY);
  const push = overlayPct(row.endDay, row.pushDays, dayY);
  return (
    <div
      className={`pipeline-gantt-row clickable${selected ? ' selected' : ''}`}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      role="button"
      tabIndex={0}
      aria-label={`${row.costCodes.length ? `${row.costCodes.join(', ')} ` : row.costCode ? `${row.costCode} ` : ''}${row.name}`}
    >
      <div className="pipeline-gantt-label">
        {row.costCodes.length > 0 && (
          <div className="pipeline-gantt-codes">
            {row.costCodes.map((code) => (
              <span key={code} className="pipeline-gantt-code">{code}</span>
            ))}
          </div>
        )}
        <div className="pipeline-gantt-label-main">
          <span className="pipeline-gantt-label-name">{row.name}</span>
          <span className="pipeline-gantt-lifecycle">{row.lifecycleLabel}</span>
        </div>
      </div>
      <div className="pipeline-gantt-bars">
        {row.leftPct !== null && row.widthPct !== null ? (
          <div
            className={`pipeline-gantt-bar ${BAR_CLASS[status]}`}
            style={{ left: `${row.leftPct}%`, width: `${row.widthPct}%` }}
          >
            {row.percentComplete !== null && row.percentComplete > 0 ? `${row.percentComplete}%` : ''}
          </div>
        ) : (
          <span className="pipeline-gantt-unscheduled">Unscheduled</span>
        )}
        {row.actualLeftPct !== null && row.actualWidthPct !== null && (
          <div
            className="pipeline-gantt-actual"
            style={{ left: `${row.actualLeftPct}%`, width: `${row.actualWidthPct}%` }}
            data-testid="gantt-actual"
          />
        )}
        {slip && (
          <div
            className="pipeline-gantt-slip"
            style={{ left: `${slip.leftPct}%`, width: `${slip.widthPct}%` }}
            data-testid="gantt-slip"
          />
        )}
        {push && (
          <div
            className="pipeline-gantt-slip push"
            style={{ left: `${push.leftPct}%`, width: `${push.widthPct}%` }}
            data-testid="gantt-push"
          />
        )}
      </div>
    </div>
  );
};

const PhaseDetailModal = ({
  matterid, row, visible, mayManage, onClose,
}: {
  matterid: string;
  row: PhaseGanttRow;
  visible: boolean;
  mayManage: boolean;
  onClose: () => void;
}): JSX.Element => {
  const status = displayStatus(row, visible);
  const queryClient = useQueryClient();
  const [startPick, setStartPick] = useState(row.actualStart ?? '');
  const [endPick, setEndPick] = useState(row.actualEnd ?? '');
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  // Actuals record what HAPPENED (plan W) — the picker greys the future and the server refuses
  // it regardless.
  const maxPick = new Date(todayUtcMs()).toISOString().slice(0, 10);

  const save = useMutation({
    // The pair IS the resource: both fields go every call, '' = null = "not entered", so
    // clearing a mis-entered date is the same act as entering one.
    mutationFn: () => PipelineProjectService.updatePhaseSchedule(matterid, row.id, {
      actual_start: startPick || null,
      actual_end: endPick || null,
    }),
    onSuccess: () => {
      setScheduleError(null);
      void queryClient.invalidateQueries({ queryKey: ['ProjectPhases', matterid] });
    },
    onError: (err: unknown) =>
      setScheduleError((err as Error)?.message || 'Could not save the actual dates.'),
  });
  const over = visible && row.projectedVariance !== undefined && row.projectedVariance < 0;
  const duration = row.durationDays !== null
    ? `${row.durationDays} d${row.slipDays > 0 ? ` (+${row.slipDays} slip)` : ''}${
      row.pushDays > 0 ? ` (+${row.pushDays} push)` : ''}`
    : '—';
  const crewBits = [
    row.crewActual !== null ? `${row.crewActual} on site` : null,
    row.crewPlanned !== null ? `${row.crewPlanned} planned` : null,
  ].filter(Boolean);

  return (
    <div
      className="pipeline-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${row.costCode ?? ''} ${row.name}`.trim()}
    >
      <div className="pipeline-modal pipeline-modal-phase">
        <div className="pipeline-modal-head">
          <div className="pipeline-gantt-detail-header">
            <h3 className="pipeline-modal-title">
              <FontAwesomeIcon icon={faProjectDiagram} />
              {row.costCodes.length > 0 && (
                <span className="pipeline-gantt-codes">
                  {row.costCodes.map((code) => (
                    <span key={code} className="pipeline-gantt-code">{code}</span>
                  ))}
                </span>
              )}
              {row.name}
            </h3>
            <span className={`pipeline-gantt-badge ${status}`}>
              <span className={`pipeline-gantt-dot ${status}`} />
              {STATUS_LABEL[status]}
            </span>
          </div>
          <button type="button" className="pipeline-modal-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="pipeline-modal-body">
          <div className="pipeline-gantt-drill">
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Window</span>
              <span className="pipeline-gantt-stat-value">
                {row.windowStart && row.windowEnd
                  ? `${row.windowStart} → ${row.windowEnd}` : '—'}
              </span>
            </div>
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Actual</span>
              <span className="pipeline-gantt-stat-value">
                {row.actualStart || row.actualEnd
                  ? `${row.actualStart ?? '—'} → ${row.actualEnd ?? '—'}` : 'Not entered'}
                <span className="pipeline-gantt-lifecycle"> · {row.lifecycleLabel}</span>
              </span>
            </div>
            {/* Sheet EVIDENCE beside the entered pair — when they disagree ("first sheet 12 Aug,
                actual start entered 14 Aug") both render and neither wins silently. */}
            {row.firstSheetDate && (
              <div className="pipeline-gantt-stat">
                <span className="pipeline-gantt-stat-label">Day sheets</span>
                <span className="pipeline-gantt-stat-value">
                  {row.firstSheetDate === row.lastSheetDate
                    ? row.firstSheetDate
                    : `${row.firstSheetDate} → ${row.lastSheetDate}`}
                </span>
              </div>
            )}
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Duration</span>
              <span className="pipeline-gantt-stat-value">{duration}</span>
            </div>
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Complete</span>
              <span className="pipeline-gantt-stat-value">
                {row.percentComplete !== null ? `${row.percentComplete}%` : '—'}
              </span>
            </div>
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Budget</span>
              <span className="pipeline-gantt-stat-value">{money(row.planned)}</span>
            </div>
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Projected</span>
              <span className={`pipeline-gantt-stat-value${over ? ' bad' : ''}`}>
                {money(row.projected)}
              </span>
            </div>
            <div className="pipeline-gantt-stat">
              <span className="pipeline-gantt-stat-label">Variance</span>
              <span className={`pipeline-gantt-stat-value${over ? ' bad' : ''}`}>
                {money(row.projectedVariance)}
              </span>
            </div>
          </div>
          <div className="pipeline-gantt-detail-title">
            <FontAwesomeIcon icon={faUsers} /> Crew
          </div>
          <p className="pipeline-muted">
            {crewBits.length > 0 ? crewBits.join(' · ') : 'No crew recorded for this phase.'}
          </p>

          {mayManage && (
            <form
              className="pipeline-form"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <div className="pipeline-gantt-detail-title">Actual dates</div>
              <p className="pipeline-panel-subtitle">
                When the phase really ran. The planned window stays frozen — scope or price
                changes go through a Change Order.
              </p>
              <div className="pipeline-form-row">
                <label>
                  Actual start
                  <input
                    type="date"
                    value={startPick}
                    max={maxPick}
                    onChange={(e) => setStartPick(e.target.value)}
                  />
                </label>
                <label>
                  Actual end
                  <input
                    type="date"
                    value={endPick}
                    max={maxPick}
                    onChange={(e) => setEndPick(e.target.value)}
                  />
                </label>
              </div>
              {scheduleError && (
                <p className="pipeline-form-error" role="alert">{scheduleError}</p>
              )}
              <div className="pipeline-form-actions">
                <button
                  type="submit"
                  className="pipeline-action-btn"
                  disabled={save.isPending}
                >
                  Save actual dates
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
